import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, Redirect } from "react-router-dom";
import Axios from "axios";
import {
  FaBed,
  FaChevronRight,
  FaDoorOpen,
  FaIdCard,
  FaPlus,
  FaRupeeSign,
  FaSearch,
  FaSyncAlt,
  FaUtensils,
} from "react-icons/fa";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import COLORS from "../resources/colors/COLORS";
import SelectClass from "../ChildComponent/SelectClass";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const SERVICE_ID = "SRV002";

// Created By Cursor
function studentInitials(name) {
  const parts = String(name || "?").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

// Created By Cursor
function money(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString("en-IN") : value ?? "0";
}

const StudentAvatar = memo(function StudentAvatar({ name, imagePath }) {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [imagePath]);

  const showImg = Boolean(imagePath) && !imgFailed;

  return (
    <div className="ts-card__avatar" aria-hidden>
      {showImg ? (
        <img src={imagePath} alt="" onError={() => setImgFailed(true)} />
      ) : (
        <span className="ts-card__initials">{studentInitials(name)}</span>
      )}
    </div>
  );
});

const HostelStudentCard = memo(function HostelStudentCard({
  student,
  index,
  onOpen,
}) {
  const hostel = student?.hostel_info?.[0] || null;
  const rollNo = student?.class?.[0]?.student_roll_no || "—";
  const classId = student?.class?.[0]?.class_id;
  const sessionId = student?.class?.[0]?.session_id;
  const enroll = student?.student_enrollment_no;

  if (!hostel || !classId || !sessionId || !enroll) return null;

  const detailsPath = `/AdminMain/HostelStudentDetails/${enroll}/${classId}/${sessionId}/${SERVICE_ID}`;

  return (
    <Link
      to={detailsPath}
      className="ts-card"
      style={{ "--ts-i": index }}
      onClick={() => onOpen(student)}
    >
      <span className="ts-card__index" aria-hidden>
        {String(index + 1).padStart(2, "0")}
      </span>

      <StudentAvatar
        name={student.stu_name}
        imagePath={student.student_img_path}
      />

      <div className="ts-card__main min-w-0">
        <div className="ts-card__top">
          <div className="ts-card__who min-w-0">
            <h3 className="ts-card__name">{student.stu_name || "N/A"}</h3>
            <p className="ts-card__father">
              {student.father_name ? `S/O ${student.father_name}` : "—"}
            </p>
          </div>
        </div>

        <div className="ts-card__chips">
          <span className="ts-chip ts-chip--enroll">
            <FaIdCard aria-hidden />
            {enroll || "N/A"}
            <span className="ts-chip__sep" aria-hidden>
              ·
            </span>
            Roll {rollNo}
          </span>
          <span className="ts-chip ts-chip--route">
            <FaDoorOpen aria-hidden />
            Room {hostel.room_no || "—"}
          </span>
          <span className="ts-chip ts-chip--vehicle">
            <FaBed aria-hidden />
            Block {hostel.block_id || "—"}
          </span>
          <span className="ts-chip ts-chip--point">
            <FaRupeeSign aria-hidden />
            Caution ₹ {money(hostel.caution_money)}
          </span>
          <span className="ts-chip ts-chip--time">
            <FaUtensils aria-hidden />
            Mess: {hostel.is_mess_taken === "Yes" ? "Yes" : "No"}
          </span>
        </div>
      </div>

      <span className="ts-card__go">
        Details <FaChevronRight aria-hidden />
      </span>
    </Link>
  );
});

export default function HostelStudent() {
  const loggedIn = localStorage.getItem("token") !== null;

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedClassPosition, setSelectedClassPosition] = useState(-1);
  const [className, setClassName] = useState("");
  const [sectionName, setSectionName] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const getHostelStudents = useCallback(
    async ({ soft = false } = {}) => {
      if (!classId || !sectionId || !sessionId) {
        setStudents([]);
        return;
      }

      if (soft) setRefreshing(true);
      else setLoading(true);

      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        setStudents([]);
        myToast.notify_warning("School code is missing.");
        setLoading(false);
        setRefreshing(false);
        return;
      }

      try {
        const response = await Axios.get(`${API_BASE()}getHostelStudent.php`, {
          params: {
            school_code: schoolCode,
            class_id: classId,
            section_id: sectionId,
            session_id: sessionId,
            service_id: SERVICE_ID,
          },
        });

        if (response.data?.api_status === "OK") {
          setStudents(response.data.data || []);
        } else {
          setStudents([]);
          if (response.data?.api_remark) {
            myToast.notify_warning(response.data.api_remark);
          }
        }
      } catch (error) {
        console.error("Error fetching hostel students:", error);
        setStudents([]);
        myToast.notify_warning("Network error loading hostel students.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [classId, sectionId, sessionId]
  );

  useEffect(() => {
    if (selectedClassPosition >= 0 && classId && sectionId && sessionId) {
      getHostelStudents();
    }
  }, [
    selectedClassPosition,
    classId,
    sectionId,
    sessionId,
    getHostelStudents,
  ]);

  const callBackClassSelect = useCallback(
    (
      class_id,
      section_id,
      session_id,
      selected_position,
      class_name,
      section_name,
      _session_name,
      START_DATE
    ) => {
      setClassId(class_id);
      setClassName(class_name || "");
      setSectionName(section_name || "");
      setSectionId(section_id);
      setSessionId(session_id);
      setStartDate(START_DATE || "");
      setSelectedClassPosition(selected_position);
      setSearchTerm("");
    },
    []
  );

  const onClickedStudent = useCallback(
    (student) => {
      localStorage.setItem("HostelData", JSON.stringify(student));
      localStorage.setItem("START_DATE", startDate);
    },
    [startDate]
  );

  const onClickedAddHostel = useCallback(() => {
    localStorage.setItem("START_DATE", startDate);
  }, [startDate]);

  const filteredStudents = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return students;
    return students.filter((student) => {
      const hostel = student?.hostel_info?.[0] || {};
      const hay = [
        student.stu_name,
        student.student_enrollment_no,
        student.father_name,
        student.class?.[0]?.student_roll_no,
        hostel.room_no,
        hostel.block_id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [students, searchTerm]);

  if (!loggedIn) return <Redirect to="/Login" />;

  const classSelected = selectedClassPosition >= 0 && classId && sectionId;
  const classLabel = [className, sectionName].filter(Boolean).join(" · ");

  return (
    <div
      className="content payroll-page sm-page ts-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Hostel Student</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaBed />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Hostel Students</h1>
              <p className="sm-hero__sub">
                View and manage students assigned to hostel
              </p>
            </div>
          </div>

          <div className="sm-hero__tools">
            <div className="sm-hero__search-group">
              <FaSearch className="ts-search-icon" aria-hidden />
              <input
                type="search"
                className="sm-hero__search"
                placeholder="Search students…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search hostel students"
                disabled={!classSelected}
              />
            </div>
            <button
              type="button"
              onClick={() => getHostelStudents({ soft: true })}
              disabled={refreshing || !classSelected}
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh list"
              aria-label="Refresh list"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <Link
              to="/AdminMain/AddHostelStudent"
              className="ts-hero-link ts-hero-link--solid"
              onClick={onClickedAddHostel}
            >
              <FaPlus aria-hidden /> Add Student
            </Link>
          </div>
        </header>

        <div className="ts-content">
          <div className="ts-class-wrap">
            <SelectClass
              action={callBackClassSelect}
              selected_position={selectedClassPosition}
            />
          </div>

          <div className="ts-section-head">
            <div className="min-w-0">
              <h2 className="ts-section-head__title">Assigned students</h2>
              <p className="ts-section-head__sub">
                {classSelected
                  ? classLabel || "Selected class"
                  : "Choose a class to load the list"}
              </p>
            </div>
            <span className="ts-section-head__count">
              {classSelected
                ? `${filteredStudents.length}${
                    searchTerm.trim() ? ` of ${students.length}` : ""
                  } student${filteredStudents.length === 1 ? "" : "s"}`
                : "—"}
            </span>
          </div>

          {loading ? (
            <div className="ts-loading">
              <MyLoader />
            </div>
          ) : !classSelected ? (
            <div className="sm-empty ts-empty">
              <div className="ts-empty__circle" aria-hidden>
                <FaBed />
              </div>
              <p className="sm-empty__title">Select a class</p>
              <p className="sm-empty__hint">
                Choose class and section above to view hostel students.
              </p>
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="sm-empty ts-empty">
              <div className="ts-empty__circle" aria-hidden>
                <FaBed />
              </div>
              <p className="sm-empty__title">
                {students.length === 0
                  ? "No hostel students found"
                  : "No matches for your search"}
              </p>
              <p className="sm-empty__hint">
                {students.length === 0
                  ? "No students are assigned to hostel for this class."
                  : "Try a different name, enrollment, or room number."}
              </p>
              {students.length === 0 ? (
                <Link
                  to="/AdminMain/AddHostelStudent"
                  className="ts-empty-cta"
                  onClick={onClickedAddHostel}
                >
                  <FaPlus aria-hidden /> Add Student
                </Link>
              ) : null}
            </div>
          ) : (
            <div className="ts-list">
              {filteredStudents.map((student, idx) => (
                <HostelStudentCard
                  key={
                    student.student_enrollment_no ||
                    student.stu_id ||
                    `student-${idx}`
                  }
                  student={student}
                  index={idx}
                  onOpen={onClickedStudent}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
